import type { ParsedDataset, ColumnMap } from "@/lib/lytick/csv-import";
import { deserializeColumns, type CandleColumns } from "@/lib/lytick/columnar";
import { buildAllTiers } from "@/lib/lytick/aggregate-columns";
import { readFullDatasetFromOpfs } from "@/lib/lytick/opfs-store";
import { db } from "@/lib/lytick/db";
import { useUIStore } from "@/stores/uiStore";

/** Metadata posted back by the worker (no column data). */
interface WorkerMeta {
  contentHash: string;
  symbol: string;
  granularitySeconds: number;
  rowCount: number;
  firstTs: number;
  lastTs: number;
  warnings: string[];
  usedFallback: boolean;
  headers: string[];
  columnMap: ColumnMap;
  filenameTimeframe?: string;
  filenameRangeStart?: number;
  filenameRangeEnd?: number;
  tierTfMinutes: number[];
}

type WorkerMessage =
  | { type: "progress"; pct: number }
  | { type: "result"; stored: "opfs"; meta: WorkerMeta }
  | {
      type: "result";
      stored: "memory";
      meta: WorkerMeta;
      baseBuffer: ArrayBuffer;
      tierBuffers: { tfMinutes: number; buffer: ArrayBuffer }[];
      opfsWriteFailed: boolean;
    }
  | { type: "error"; message: string };

/**
 * Parse an MT5 CSV file in a dedicated Web Worker.
 * Returns a ParsedDataset with CandleColumns in memory, persisted to OPFS or Dexie.
 */
export function parseMt5CsvInWorker(
  file: File,
  onProgress?: (pct: number) => void,
  options?: { columnMap?: ColumnMap },
): Promise<ParsedDataset> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(
      new URL("../../workers/csvParser.worker.ts", import.meta.url),
      { type: "module" },
    );

    worker.onmessage = async (e: MessageEvent<WorkerMessage>) => {
      const { data } = e;

      if (data.type === "progress") {
        onProgress?.(data.pct);
        return;
      }

      if (data.type === "error") {
        worker.terminate();
        reject(new Error(data.message));
        return;
      }

      // type === "result"
      worker.terminate();

      try {
        const { meta } = data;

        if (data.stored === "opfs") {
          // Read back from OPFS (worker already persisted everything there)
          const stored = await readFullDatasetFromOpfs(meta.contentHash);
          if (!stored) throw new Error("OPFS write succeeded but read-back failed.");
          resolve(metaToDataset(meta, stored.base, stored.tiers));
        } else {
          // data.stored === "memory": persist to Dexie, build tiers from transferred buffers
          const base = deserializeColumns(data.baseBuffer);
          const tiers = new Map(
            data.tierBuffers.map(({ tfMinutes, buffer }) => [
              tfMinutes,
              deserializeColumns(buffer),
            ]),
          );

          // Build any tiers that are missing (safety net for gaps)
          for (const [k, v] of buildAllTiers(base, meta.granularitySeconds)) {
            if (!tiers.has(k)) tiers.set(k, v);
          }

          // Persist base to Dexie for future restores
          await db.csvDatasets
            .put({
              contentHash: meta.contentHash,
              manifest: {
                contentHash: meta.contentHash,
                symbol: meta.symbol,
                granularitySeconds: meta.granularitySeconds,
                rowCount: meta.rowCount,
                firstTs: meta.firstTs,
                lastTs: meta.lastTs,
                savedAt: Date.now(),
                tierTfMinutes: meta.tierTfMinutes,
              },
              baseBuffer: data.baseBuffer,
              savedAt: Date.now(),
            })
            .catch(() => {
              /* non-critical — user can re-import */
            });

          if (data.opfsWriteFailed) {
            useUIStore
              .getState()
              .addToast(
                "Dataset saved to fallback storage — OPFS write failed. Data may not survive a browser storage clear.",
                "warning",
              );
          }

          resolve(metaToDataset(meta, base, tiers));
        }
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    };

    worker.onerror = (err) => {
      worker.terminate();
      reject(new Error(err.message));
    };

    worker.postMessage({ file, options });
  });
}

function metaToDataset(
  meta: WorkerMeta,
  base: CandleColumns,
  tiers: Map<number, CandleColumns>,
): ParsedDataset {
  return {
    columns: base,
    tiers,
    symbol: meta.symbol,
    granularitySeconds: meta.granularitySeconds,
    rowCount: meta.rowCount,
    firstTs: meta.firstTs,
    lastTs: meta.lastTs,
    contentHash: meta.contentHash,
    warnings: meta.warnings,
    usedFallback: meta.usedFallback,
    headers: meta.headers,
    columnMap: meta.columnMap,
    ...(meta.filenameTimeframe ? { filenameTimeframe: meta.filenameTimeframe } : {}),
    ...(meta.filenameRangeStart != null ? { filenameRangeStart: meta.filenameRangeStart } : {}),
    ...(meta.filenameRangeEnd != null ? { filenameRangeEnd: meta.filenameRangeEnd } : {}),
  };
}
